import { useContext, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@zg-rentals/react-query5';

import { Box, Label, Paragraph, Radio, Text } from '@zillow/constellation-10';

import { ModularListingContext } from '../../../../containers/CreateListingPage/context';
import { WizardContext } from '../../../Wizard/context';

import { useSelector } from '../../../../selectors/useSelector';

import { useForm } from '../../../../hooks';

import actions from '../../../../containers/CreateListingPage/actions';

import PostingActions from '../../../../actions/PostingActions';
import PropertiesActions from '../../../../actions/PropertiesActions';
import wizardActions from '../../../Wizard/actions';

import { postingConstraints } from '../../../../utils/formValidation';
import propertyUtils from '../../../../utils/propertyUtils';

import { GET_LISTING_RECS } from '../../../../constants/RecommendationsConstants';
import { DISPLAY_SERVER_ERROR_AT } from '../../../../constants/WizardConstants';

import WizardServerError from '../../../../model/WizardServerError';

import { WizardLabeledControl } from '../../../../styles/wizard';

const HideAddressReviewFormField = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { reducerWizardDispatch } = useContext(WizardContext);

  const {
    properties: {
      currentProperty,
      currentProperty: { listingAlias, hideAddress },
    },
  } = useSelector((state) => state);
  const {
    modularListingState: { isInModalState, isSaveComplete, serverErrorObj, shouldSaveOnBlur },
    reducerDispatch,
  } = useContext(ModularListingContext);

  const {
    inputs,
    hasFieldError,
    handleChange: handleFormChange,
  } = useForm({ hideAddress: hideAddress.toString() }, postingConstraints);

  useEffect(() => {
    if (isSaveComplete) {
      save({
        target: {
          name: 'hideAddress',
          // @ts-expect-error
          value: inputs.hideAddress,
        },
      });
    }
  }, [isSaveComplete]);

  const clearServerError = () => {
    if (serverErrorObj.fieldName) {
      actions.removeServerError({
        reducerDispatch,
      });
    }
  };

  const save = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name: fieldName } = event.target;

    if (!shouldSaveOnBlur && !isSaveComplete) {
      return;
    }

    if (hasFieldError(fieldName, value)) {
      return;
    }

    const dataToSave = {
      fieldName,
      value,
      propertyId: listingAlias,
    };

    wizardActions.updateIsFetchingData({ reducerWizardDispatch, isFetchingData: true });

    dispatch(PostingActions.property.edit(dataToSave))
      // @ts-expect-error
      .then(() => {
        dispatch(PropertiesActions.updateCurrentProperty({ [fieldName]: value === 'true' }));
        queryClient.invalidateQueries({ queryKey: [GET_LISTING_RECS] });
        // reset save cta
        actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
      })
      .catch((message: string) => {
        updateServerError(message, fieldName);
      })
      .finally(() => {
        wizardActions.updateIsFetchingData({
          reducerWizardDispatch,
          isFetchingData: false,
        });
      });
  };

  const updateServerError = (message = '', fieldName = '') => {
    const displayAt = isInModalState ? DISPLAY_SERVER_ERROR_AT.FIELD : DISPLAY_SERVER_ERROR_AT.PAGE;

    actions.updateServerError({
      reducerDispatch,
      serverErrorObj: WizardServerError({
        displayAt,
        fieldName,
        message,
        displayMessage: message,
      }),
    });
  };

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.persist();
    clearServerError();
    // @ts-expect-error
    handleFormChange(event);
  };

  return (
    <>
      <Paragraph textStyle="body-sm-bold" css={{ marginBottom: '200' }}>
        {propertyUtils.formatAddress(currentProperty).formattedCompleteAddress}
      </Paragraph>
      <Text>Hide property address</Text>
      <Box css={{ display: 'flex', paddingLeft: '200', flexDirection: 'column' }}>
        <WizardLabeledControl
          controlId="false"
          aria-label="Show address"
          label={
            <Label>
              No&nbsp;
              <strong>(Recommended)</strong>
            </Label>
          }
          control={
            <Radio
              name="hideAddress"
              value="false"
              checked={inputs.hideAddress === 'false'}
              onChange={handleRadioChange}
            />
          }
        />

        <WizardLabeledControl
          controlId="true"
          aria-label="Hide address"
          label={<Label>Yes</Label>}
          control={
            <Radio
              name="hideAddress"
              value="true"
              checked={inputs.hideAddress === 'true'}
              onChange={handleRadioChange}
            />
          }
        />
      </Box>
      <Box css={{ marginTop: '800' }}>
        <Paragraph textStyle="body-sm-bold" css={{ marginBottom: '200' }}>
          Hiding the property address
        </Paragraph>
        <Paragraph>
          You can hide the address if privacy is a concern, but the listing may receive fewer views and contacts than
          listings that show the property address.
        </Paragraph>
      </Box>
    </>
  );
};

export default HideAddressReviewFormField;
